import { useState } from "react";

import {
  FilterOutlined,
  ExportOutlined,
  LoginOutlined,
  LogoutOutlined,
  CoffeeOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm text-gray-500">{label}</span>
    <span className={`text-2xl font-bold ${color}`}>{value}</span>
  </div>
);

const InfoCard = ({ icon, title, time, subtitle, badge, badgeColor }: any) => {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm transition-shadow duration-300 hover:shadow-md">
      {/* Top Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          {/* Icon */}
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg">
            {icon}
          </div>

          {/* Text */}
          <div>
            <p className="text-sm font-medium">{title}</p>

            {/* Time (kept together) */}
            <p className="mt-1 text-lg font-bold leading-tight">{time}</p>

            {/* Subtitle */}
            {subtitle && (
              <p className="mt-1 text-xs leading-relaxed text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Badge */}
        {badge && (
          <span
            className={`self-start rounded-full px-3 py-1 text-xs font-medium sm:self-auto ${badgeColor}`}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
  );
};

const TodaysHoursPage = () => {
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [selectedDate, setSelectedDate] = useState("2025-12-16");

  const handleExport = () => {
    const payload = {
      date: selectedDate,
      employee: selectedEmployee,
    };

    console.log("Export payload:", payload);

    // Example API call
    // exportAttendance(payload)
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="">
          <span className="text-2xl font-bold text-[#111827]">
            Today&#39;s Hours
          </span>
          <p className="text-sm text-[#627384]">
            Realtime attendance tracking and biometric data report&apos;s
            details.
          </p>
        </div>

        <button
          className="border-primary hover:bg-primary flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:text-white"
          onClick={handleExport}
        >
          <ExportOutlined />
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-white p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <FilterOutlined />
          Filters
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <select
            value={selectedEmployee}
            onChange={e => setSelectedEmployee(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">All Employees</option>
            <option value="EMP001">EMP001</option>
            <option value="EMP002">EMP002</option>
          </select>

          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Daily Summary */}
      <div className="rounded-xl border bg-[linear-gradient(180deg,#fff,#ff9d000d)] p-4">
        <p className="mb-3 font-medium">Daily Summary</p>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <StatCard label="Total Hours" value="8.3h" color="text-orange-600" />
          <StatCard
            label="Break Duration"
            value="0.75h"
            color="text-blue-600"
          />
          <StatCard
            label="Productive Hours"
            value="7.55h"
            color="text-green-600"
          />
        </div>
      </div>

      {/* Check-in / Check-out */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <InfoCard
          icon={<LoginOutlined className="text-green-600" />}
          title="Check-In"
          time="09:05 AM"
          subtitle="Biometric ID: EMP001 verified"
          badge="On Time"
          badgeColor="bg-green-100 text-green-700"
        />

        <InfoCard
          icon={<LogoutOutlined className="text-red-500" />}
          title="Check-Out"
          time="06:10 PM"
          subtitle="Biometric ID: EMP001 verified"
          badge="Completed"
          badgeColor="bg-red-100 text-red-600"
        />
      </div>

      {/* Breaks */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <InfoCard
          icon={<CoffeeOutlined className="text-orange-500" />}
          title="Break Started"
          time="12:30 PM"
          subtitle="Duration: 0.75 hours"
          badge="Lunch Break"
          badgeColor="bg-orange-100 text-orange-700"
        />

        <InfoCard
          icon={<ClockCircleOutlined className="text-blue-500" />}
          title="Break Ended"
          time="01:15 PM"
          subtitle="Back to work"
          badge="Resumed"
          badgeColor="bg-blue-100 text-blue-700"
        />
      </div>

      {/* Attendance Status */}
      <div className="rounded-xl border bg-white p-4">
        <h2 className="mb-4 font-medium">Attendance Status</h2>

        <div className="mb-4 flex items-center justify-between rounded-lg bg-gray-50 p-3">
          <div>
            <p className="text-sm font-medium">Current Status</p>
            <p className="text-xs text-gray-500">Active session in progress</p>
          </div>

          <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
            Working
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-lg border p-4">
            <p className="text-sm text-gray-500">Expected Hours</p>
            <p className="text-xl font-semibold">9.0h</p>
          </div>

          <div className="rounded-lg border p-4">
            <p className="text-sm text-gray-500">Overtime</p>
            <p className="text-xl font-semibold text-orange-600">+0.3h</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodaysHoursPage;
